import { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Document24Regular,
  ChevronRight20Regular,
  ArrowLeft16Regular,
  Search20Regular,
  DocumentSearch20Regular,
  Sparkle24Regular,
  Chat24Regular,
  ClipboardSearch24Regular,
  PersonAccounts24Regular,
  Lightbulb24Regular,
  ArrowRight16Regular,
  BookOpen24Regular,
  Rocket24Regular,
  TargetArrow24Regular,
  PeopleTeam24Regular,
  ShieldCheckmark24Regular,
  DataUsage24Regular,
  SlashForward24Regular,
  ArrowUpload24Regular,
  ShareAndroid24Regular,
  Star24Regular,
  CheckmarkCircle24Regular,
} from "@fluentui/react-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { helpCategories, HelpArticle } from "./helpContent";
import styles from "./Help.module.css";

/* ── Feature card data for visual landing ── */
const featureCards = [
  {
    icon: <Chat24Regular />,
    title: "Chat with AURA",
    desc: "Ask product questions about VRP, VCO & VE. Get cited answers grounded in real documentation.",
    color: "#004b87",
    articleId: "overview",
  },
  {
    icon: <TargetArrow24Regular />,
    title: "Response Modes",
    desc: "Simple for L1 support, Standard for daily use, Enhanced for deep technical review.",
    color: "#00b2a9",
    articleId: "response-modes",
  },
  {
    icon: <SlashForward24Regular />,
    title: "Slash Commands",
    desc: "Type / to access PRD, Epic, Story, EARS and diagram templates instantly.",
    color: "#470a68",
    articleId: "slash-commands",
  },
  {
    icon: <Sparkle24Regular />,
    title: "Agents",
    desc: "Switch between Product Knowledge Base and specialized agents like RFP Intelligence.",
    color: "#ff7900",
    articleId: "agents",
  },
  {
    icon: <ArrowUpload24Regular />,
    title: "File Upload",
    desc: "Attach PDF, DOCX, images and more (up to 20 MB). AURA extracts and analyzes content.",
    color: "#a6192e",
    articleId: "attach-files",
  },
  {
    icon: <ShareAndroid24Regular />,
    title: "Share & Export",
    desc: "Share read-only links, export to PDF, DOCX, TXT or XLSX. Rate responses with feedback.",
    color: "#17a7ff",
    articleId: "share-export",
  },
];

/* ── Walkthrough steps ── */
const walkthroughSteps = [
  {
    num: 1,
    title: "Pick a mode",
    desc: "Choose Simple, Standard, or Enhanced before chatting. This sets the system prompt for the entire conversation.",
    icon: <Rocket24Regular />,
  },
  {
    num: 2,
    title: "Ask a specific question",
    desc: "Name the product (VRP, VCO, VE), include a version, and describe what you need. Specificity drives accuracy.",
    icon: <Lightbulb24Regular />,
  },
  {
    num: 3,
    title: "Review citations & follow up",
    desc: "Every answer includes source links and 3 suggested follow-ups. Click them to go deeper — no retyping needed.",
    icon: <CheckmarkCircle24Regular />,
  },
];

/* ── Pro tips ── */
const proTips = [
  { emoji: "🎯", text: "Name one product per prompt — VRP, VCO, or VE — so the router picks the right index." },
  { emoji: "🔄", text: "After ~20 turns, start a new conversation. Context quality degrades over long chats." },
  { emoji: "📊", text: "Ask for \"comparison table\" explicitly — AURA generates structured HTML tables." },
  { emoji: "⚡", text: "Chain templates: /vrp initiative → /vrp bulk prds → /vrp prd → /vrp stories" },
];

/* ── Category icon map ── */
const categoryIcons: Record<string, JSX.Element> = {
  "getting-started": <Rocket24Regular />,
  features: <Sparkle24Regular />,
  tips: <Lightbulb24Regular />,
  troubleshooting: <ShieldCheckmark24Regular />,
};

export default function Help() {
  const location = useLocation();
  const [view, setView] = useState<"landing" | "docs">("landing");
  const [activeCategoryKey, setActiveCategoryKey] = useState(helpCategories[0].key);
  const [activeArticle, setActiveArticle] = useState<HelpArticle | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  /* Deep-link: open a specific article when navigated with state.articleId (e.g. from tour) */
  useEffect(() => {
    const state = location.state as { articleId?: string } | null;
    if (state?.articleId) {
      for (const cat of helpCategories) {
        const found = cat.articles.find((a) => a.id === state.articleId);
        if (found) {
          setActiveCategoryKey(cat.key);
          setActiveArticle(found);
          setView("docs");
          setSearchQuery("");
          break;
        }
      }
      // Clear the state so refreshing doesn't re-trigger
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const activeCategory = helpCategories.find((c) => c.key === activeCategoryKey) ?? helpCategories[0];

  const filteredArticles = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return activeCategory.articles;
    const allArticles = helpCategories.flatMap((c) => c.articles);
    return allArticles.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.summary.toLowerCase().includes(q) ||
        a.content.toLowerCase().includes(q)
    );
  }, [searchQuery, activeCategory.articles]);

  /* Navigate to a specific article (from feature card or anywhere) */
  const navigateToArticle = (articleId: string) => {
    for (const cat of helpCategories) {
      const found = cat.articles.find((a) => a.id === articleId);
      if (found) {
        setActiveCategoryKey(cat.key);
        setActiveArticle(found);
        setView("docs");
        setSearchQuery("");
        return;
      }
    }
  };

  const handleCategoryClick = (key: string) => {
    setActiveCategoryKey(key);
    setActiveArticle(null);
    setSearchQuery("");
  };

  const handleArticleClick = (article: HelpArticle) => {
    setActiveArticle(article);
  };

  const handleBack = () => {
    setActiveArticle(null);
  };

  const handleBackToLanding = () => {
    setView("landing");
    setActiveArticle(null);
    setSearchQuery("");
  };

  /* ═══════════════════════════════════════════
     LANDING VIEW — cards, walkthrough, tips
     ═══════════════════════════════════════════ */
  if (view === "landing") {
    return (
      <div className={styles.helpPage}>
        {/* ── Hero ── */}
        <div className={styles.hero}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>Help Center</h1>

            {/* Search */}
            <div className={styles.heroSearch}>
              <span className={styles.searchIcon}>
                <Search20Regular />
              </span>
              <input
                type="text"
                className={styles.heroSearchInput}
                placeholder="Search for topics, features, or questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && searchQuery.trim()) {
                    setView("docs");
                  }
                }}
              />
            </div>

            {/* Quick-access chips */}
            <div className={styles.heroChips}>
              <span className={styles.heroChip} onClick={() => { handleCategoryClick("getting-started"); setView("docs"); }}>
                <Rocket24Regular /> Getting Started
              </span>
              <span className={styles.heroChip} onClick={() => { handleCategoryClick("features"); setView("docs"); }}>
                <Sparkle24Regular /> Features
              </span>
              <span className={styles.heroChip} onClick={() => { handleCategoryClick("tips"); setView("docs"); }}>
                <Lightbulb24Regular /> Tips
              </span>
            </div>
          </div>
        </div>

        <div className={styles.landingBody}>
          {/* ── Quick Start Walkthrough ── */}
          <section className={styles.walkthroughSection}>
            <h2 className={styles.sectionHeading}>
              <Rocket24Regular className={styles.sectionIcon} />
              Get started in 3 steps
            </h2>
            <div className={styles.walkthroughGrid}>
              {walkthroughSteps.map((step, idx) => (
                <>
                  {idx > 0 && (
                    <div key={`arrow-${idx}`} className={styles.stepArrow}>
                      <span className={styles.stepArrowIcon}>&#x276F;</span>
                    </div>
                  )}
                  <div key={step.num} className={styles.walkthroughCard}>
                    <div className={styles.stepNumber}>{step.num}</div>
                    <div className={styles.stepIcon}>{step.icon}</div>
                    <h3 className={styles.stepTitle}>{step.title}</h3>
                    <p className={styles.stepDesc}>{step.desc}</p>
                  </div>
                </>
              ))}
            </div>
          </section>

          {/* ── Feature Cards ── */}
          <section className={styles.featuresSection}>
            <h2 className={styles.sectionHeading}>
              <Sparkle24Regular className={styles.sectionIcon} />
              Explore features
            </h2>
            <div className={styles.featureGrid}>
              {featureCards.map((card) => (
                <button
                  key={card.articleId}
                  className={styles.featureCard}
                  onClick={() => navigateToArticle(card.articleId)}
                >
                  <div
                    className={styles.featureIconWrap}
                    style={{ backgroundColor: card.color + "14", color: card.color }}
                  >
                    {card.icon}
                  </div>
                  <h3 className={styles.featureTitle}>{card.title}</h3>
                  <p className={styles.featureDesc}>{card.desc}</p>
                  <span className={styles.featureLink}>
                    Learn more <ArrowRight16Regular />
                  </span>
                </button>
              ))}
            </div>
          </section>

          {/* ── Pro Tips ── */}
          <section className={styles.tipsSection}>
            <h2 className={styles.sectionHeading}>
              <Lightbulb24Regular className={styles.sectionIcon} />
              Pro tips
            </h2>
            <div className={styles.tipsGrid}>
              {proTips.map((tip, i) => (
                <div key={i} className={styles.tipCard}>
                  <span className={styles.tipEmoji}>{tip.emoji}</span>
                  <p className={styles.tipText}>{tip.text}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════
     DOCS VIEW — original sidebar + articles
     ═══════════════════════════════════════════ */
  return (
    <div className={styles.helpPage}>
      {/* Header */}
      <div className={styles.helpHeader}>
        <div className={styles.docsHeaderRow}>
          <button className={styles.backToLandingBtn} onClick={handleBackToLanding}>
            <ArrowLeft16Regular />
            <span>Help Center</span>
          </button>
          <h1 className={styles.helpTitle}>AURA Manual</h1>
        </div>

        {/* Search */}
        <div className={styles.searchBar}>
          <span className={styles.searchIcon}>
            <Search20Regular />
          </span>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search help topics..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setActiveArticle(null);
            }}
          />
        </div>
      </div>

      {/* Mobile category chips */}
      <div className={styles.mobileCategoryBar}>
        {helpCategories.map((cat) => (
          <button
            key={cat.key}
            className={`${styles.mobileCategoryChip} ${activeCategoryKey === cat.key ? styles.mobileCategoryChipActive : ""}`}
            onClick={() => handleCategoryClick(cat.key)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className={styles.helpBody}>
        {/* Sidebar */}
        <aside className={styles.helpSidebar}>
          <p className={styles.sidebarLabel}>Browse by topic</p>
          {helpCategories.map((cat) => (
            <button
              key={cat.key}
              className={`${styles.categoryBtn} ${activeCategoryKey === cat.key ? styles.categoryBtnActive : ""}`}
              onClick={() => handleCategoryClick(cat.key)}
            >
              <span className={styles.categoryBtnInner}>
                <span className={styles.categoryIcon}>{categoryIcons[cat.key] ?? <Document24Regular />}</span>
                <span>{cat.label}</span>
              </span>
              {activeCategoryKey === cat.key && (
                <span className={styles.categoryArrow}>
                  <ChevronRight20Regular />
                </span>
              )}
            </button>
          ))}
        </aside>

        {/* Main content */}
        <div className={styles.helpMain}>
          {activeArticle ? (
            <div className={styles.articleDetail}>
              <div className={styles.markdownContent}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {activeArticle.content}
                </ReactMarkdown>
              </div>
            </div>
          ) : filteredArticles.length > 0 ? (
            <div className={styles.articleList}>
              {filteredArticles.map((article) => (
                <button
                  key={article.id}
                  className={styles.articleCard}
                  onClick={() => handleArticleClick(article)}
                >
                  <span className={styles.articleIcon}>
                    <Document24Regular />
                  </span>
                  <div className={styles.articleBody}>
                    <p className={styles.articleTitle}>{article.title}</p>
                    <p className={styles.articleSummary}>{article.summary}</p>
                  </div>
                  <span className={styles.articleArrow}>
                    <ChevronRight20Regular />
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className={styles.noResults}>
              <DocumentSearch20Regular className={styles.noResultsIcon} />
              <p className={styles.noResultsText}>
                No articles found for &ldquo;{searchQuery}&rdquo;. Try a different search term.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
